import React from 'react';
import { Modal, Button } from 'antd';
import { ThunderboltOutlined } from '@ant-design/icons';
import GmailMark from '../email/GmailMark';
import css from './AutoApplyGmailGateModal.module.scss';

/**
 * Which half of the dependency is missing:
 * - `connect` — no Gmail account linked at all.
 * - `fix`     — linked, but the grant omitted "send email on your behalf".
 *
 * Same two words the consent primer uses, so one variant travels unchanged from
 * this modal through the primer and into the Google redirect.
 */
export type AutoApplyGateVariant = 'connect' | 'fix';

interface AutoApplyGmailGateModalProps {
  open: boolean;
  variant: AutoApplyGateVariant;
  onConnect: () => void;
  onClose: () => void;
}

const COPY: Record<
  AutoApplyGateVariant,
  { title: string; body: React.ReactNode; cta: string; linkLabel: string }
> = {
  connect: {
    title: 'Auto-Apply sends from your Gmail',
    body: (
      <>
        Auto-Apply emails your applications from your own address, so replies come
        straight back to you. Connect Gmail and we'll bring you right back here.
      </>
    ),
    cta: 'Connect Gmail',
    linkLabel: 'Auto-Apply is not connected to Gmail',
  },
  fix: {
    title: 'Auto-Apply needs permission to send',
    body: (
      <>
        Your Gmail is connected, but <strong>'Send email on your behalf'</strong>{' '}
        wasn't checked. Grant it and Auto-Apply can start sending.
      </>
    ),
    cta: 'Grant permission',
    linkLabel: 'Gmail is connected but cannot send on your behalf',
  },
};

/**
 * Shown when someone reaches for Auto-Apply before Gmail can send for them.
 *
 * This is a missing connection, not an error, and it is deliberately not dressed
 * as one — no warning icon, no red. The graphic states the dependency instead:
 * the two marks the app already uses for these things, with the link between
 * them broken. The `fix` variant joins them and marks the connector instead,
 * because there the accounts ARE linked and only the permission is missing — so
 * the picture, not just the sentence, distinguishes the two states.
 *
 * Presentational only. The host owns the Gmail probe and the redirect; Connect
 * hands off to the consent primer rather than going straight to Google, because
 * skipping the primer's checkbox coaching is how a user arrives back here in the
 * `fix` variant.
 */
const AutoApplyGmailGateModal: React.FC<AutoApplyGmailGateModalProps> = ({
  open,
  variant,
  onConnect,
  onClose,
}) => {
  const { title, body, cta, linkLabel } = COPY[variant];

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onClose}
      width="min(460px, 94vw)"
      className={css.gateModal}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Not now
        </Button>,
        <Button key="connect" type="primary" onClick={onConnect}>
          {cta}
        </Button>,
      ]}
    >
      {/* One image to assistive tech, not three unlabelled glyphs. */}
      <div className={css.link} role="img" aria-label={linkLabel}>
        <span className={`${css.node} ${css.autoApplyNode}`} aria-hidden="true">
          <ThunderboltOutlined />
        </span>
        <span
          className={`${css.connector} ${variant === 'fix' ? css.partial : css.broken}`}
          aria-hidden="true"
        />
        <span
          className={`${css.node} ${variant === 'fix' ? css.gmailNodeLinked : css.gmailNodeMissing}`}
          aria-hidden="true"
        >
          <GmailMark width={28} height={21} />
        </span>
      </div>

      <p className={css.copy}>{body}</p>
    </Modal>
  );
};

export default AutoApplyGmailGateModal;
